/**
 * AIConsentGate
 * Phase 999.72 Plan 02 — D-05 / D-06.
 *
 * Wraps an AI-using feature and gates access behind:
 *   (a) onboardingComplete === true  (user finished the onboarding flow)
 *   (b) aiDocumentConsent === true   (user agreed to AI document processing)
 *
 * When either condition is absent we render a friendly CTA card pointing to
 * /onboarding — NEVER a hard redirect (that would be disorienting for veterans
 * mid-task). Non-AI features (Doc Vault, Claims list, Timeline, Profile,
 * Community, Crisis, Find Support, Organisations) must NOT be wrapped —
 * those work without AI consent per Privacy Policy v2.0 §10.
 *
 * While the hook is still loading we render a small spinner (rather than
 * flashing the gate briefly, which looks like a bug to the user).
 *
 * CMO audit 2026-08-15 (§2.1) — the loading branch used to be
 * `if (loading || !profileData) return <CircularProgress/>` with no other exit.
 * Three states landed there permanently: a profile fetch that failed (the hook
 * sets `error` and leaves `profileData` null), a fetch that never settled, and
 * a caller that could not resolve a userId. Because this gate wraps the appeal
 * letter generator, that spinner was the prime suspect for "Appeals is dead" —
 * the veteran saw a spinner and no network call ever left the browser.
 *
 * The loading branch now has a deadline and a way out: after
 * PROFILE_LOAD_TIMEOUT_MS, or on an outright error, the veteran gets a neutral
 * message and a retry button instead of an endless spinner.
 */

import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { useProfile } from '../../hooks/useProfile';

/**
 * How long we wait for the profile before telling the veteran it did not load.
 * Chosen against the API client's own 60 s ceiling (config.api.timeout): a
 * veteran should not be asked to sit through that with no information, and a
 * healthy profile read on DEV and PROD settles well inside a second.
 */
export const PROFILE_LOAD_TIMEOUT_MS = 10000;

/**
 * @param {{
 *   children: React.ReactNode,
 *   feature?: string,
 *   user?: { userId?: string, sub?: string, username?: string }
 * }} props
 */
export default function AIConsentGate({ children, feature = 'AI features', user }) {
  // The useProfile hook expects a userId; prefer the passed-in Cognito user
  // identifiers (the same resolution order used elsewhere in the app).
  const userId = user?.userId || user?.sub || user?.username;
  const { profileData, loading, error, refreshProfile } = useProfile(userId);

  const [timedOut, setTimedOut] = useState(false);
  // Bumped by the retry button so the timeout window restarts even when the
  // hook's own state does not change shape between attempts.
  const [attempt, setAttempt] = useState(0);

  // "Still waiting" = the hook has neither data nor an error to show. A missing
  // userId lands here too: useProfile short-circuits to loading=false with both
  // outputs empty, which is precisely the case that used to spin forever.
  const stillWaiting = loading || (!profileData && !error);

  useEffect(() => {
    if (!stillWaiting) {
      setTimedOut(false);
      return undefined;
    }
    setTimedOut(false);
    const timer = setTimeout(() => setTimedOut(true), PROFILE_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [stillWaiting, attempt]);

  const handleRetry = () => {
    setTimedOut(false);
    setAttempt((n) => n + 1);
    // Guarded: this component is rendered with a mocked hook in several test
    // suites, and a retry button that throws would be worse than the spinner
    // it replaces.
    if (typeof refreshProfile === 'function') refreshProfile();
  };

  // Loading affordance — avoid flashing the gate for a split second while the
  // profile loads. Uses aria-label so screen reader users know what's happening.
  if (stillWaiting && !timedOut) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={24} aria-label="Loading your profile" />
      </Box>
    );
  }

  // Failed, stalled, or no identifier to load against. Deliberately neutral and
  // non-clinical: this is a plumbing failure, and a veteran mid-appeal should
  // not read it as anything about their claim or their health.
  if (!profileData) {
    return (
      <Box
        sx={{ p: 4, maxWidth: 480, mx: 'auto', textAlign: 'center' }}
        role="alert"
      >
        <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
          We couldn't load your profile
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Please try again. The rest of the platform is still available to you.
        </Typography>
        <Button
          variant="contained"
          onClick={handleRetry}
          aria-label="Try loading your profile again"
        >
          Try again
        </Button>
      </Box>
    );
  }

  // Backend stores consent under consentSettings.aiDocumentConsent.accepted
  // (nested object with acceptedAt timestamp). Fall back to the flat form
  // for older records written before Plan 06 backend normalisation.
  const consentGiven =
    profileData.consentSettings?.aiDocumentConsent?.accepted === true ||
    profileData.aiDocumentConsent === true;
  // onboardingComplete is not currently persisted server-side as a top-level
  // field; treat absence or explicit `!== false` as "not explicitly skipped".
  // Plan 02 split the skip flag from complete flag, so a consented user who
  // never hit Skip is implicitly complete.
  const onboardingComplete =
    profileData.onboardingComplete === true ||
    (profileData.onboardingSkipped !== true && consentGiven);

  if (onboardingComplete && consentGiven) {
    return <>{children}</>;
  }

  // Friendly CTA card — D-06 explicitly forbids hard redirects. Button is a
  // plain <a> via Button href (no client-side navigate), which matches the
  // test contract and keeps the component framework-agnostic.
  return (
    <Box sx={{ p: 4, maxWidth: 480, mx: 'auto', textAlign: 'center' }}>
      <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
        Complete onboarding to unlock {feature}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        We need your consent before the AI Assistant can help with your service
        history, claims, or appeals. The rest of the platform is available to
        you now — this unlock takes about 2 minutes.
      </Typography>
      <Button
        variant="contained"
        href="/onboarding"
        aria-label="Continue onboarding"
      >
        Continue setup
      </Button>
      {/* F-19: a veteran who lands here pre-consent may be in distress — crisis
          support must never sit behind the consent gate. */}
      <Typography variant="body2" sx={{ mt: 2 }}>
        Need support right now?{' '}
        <a href="/crisis" aria-label="Crisis resources — get urgent support">
          Crisis resources are always available
        </a>
        , no setup needed.
      </Typography>
    </Box>
  );
}
